// Pestaña «Skills» de la app Agentes: las skills SKILL.md de la org.
// Cualquiera las ve; importar una (o una versión nueva) es de admins — el backend
// lo vuelve a validar (company:update). Para usar una skill se liga a un agente
// desde su editor.

import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FileUp, Library, Loader2 } from 'lucide-react';

import { importSkill, SKILLS_KEY, useOrgSkills } from './skillsApi';

const STATUS: Record<string, { label: string; cls: string }> = {
  active: { label: 'Activa', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  draft: { label: 'Borrador', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  disabled: { label: 'Deshabilitada', cls: 'bg-muted text-muted-foreground' },
};

export function SkillsPanel({ canImport, usage }: { canImport: boolean; usage: Map<string, number> }) {
  const skills = useOrgSkills();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const onFile = async (f: File) => {
    setMsg(null);
    setBusy(true);
    try {
      const r = await importSkill(await f.text());
      await qc.invalidateQueries({ queryKey: SKILLS_KEY });
      setMsg({ ok: true, text: `${r.nueva_version ? 'Se importó' : 'Sin cambios en'} «${r.skill.title}» (versión ${r.skill.version}).` });
    } catch (e) {
      const raw = e instanceof Error ? e.message : '';
      setMsg({ ok: false, text: raw.replace(/^HTTP \d+: /, '').replace(/^\{"detail":\s*"?|"?\}$/g, '').slice(0, 300) || 'No se pudo importar.' });
    } finally {
      setBusy(false);
    }
  };

  const list = skills.data ?? [];

  return (
    <section aria-label="Skills de la organización">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Una skill es un procedimiento paso a paso (formato SKILL.md). Lígala a un agente desde «Editar» y el agente la seguirá
          cuando la tarea lo pida.
        </p>
        {canImport && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".md,text/markdown,text/plain"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onFile(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-60"
            >
              {busy ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <FileUp size={15} aria-hidden />}
              Importar SKILL.md
            </button>
          </>
        )}
      </div>

      {msg && (
        <p role={msg.ok ? 'status' : 'alert'} className={`mb-3 rounded-lg px-3 py-2 text-xs ${msg.ok ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
          {msg.text}
        </p>
      )}

      {skills.isLoading && (
        <p role="status" className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 size={16} className="animate-spin" aria-hidden /> Cargando skills…
        </p>
      )}
      {skills.isError && (
        <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">No se pudieron cargar las skills.</p>
      )}
      {!skills.isLoading && !skills.isError && list.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary"><Library size={22} aria-hidden /></span>
          <p className="text-sm font-semibold text-foreground">Tu organización aún no tiene skills</p>
          <p className="max-w-sm text-xs text-muted-foreground">
            {canImport ? 'Importa un archivo SKILL.md para empezar.' : 'Pídele a un administrador que importe una.'}
          </p>
        </div>
      )}

      {list.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((s) => {
            const st = STATUS[s.status] ?? STATUS.disabled;
            const n = usage.get(s.slug) ?? 0;
            return (
              <li key={s.slug} className="orion-card flex min-w-0 flex-col rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <span aria-hidden className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                    <Library size={18} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug text-foreground">{s.title}</h3>
                    <p className="truncate font-mono text-[11px] text-muted-foreground">{s.slug} · v{s.version}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                </div>
                {(s.description || s.when_to_use) && (
                  <p className="mt-3 line-clamp-3 text-[13px] leading-relaxed text-muted-foreground">{s.description || s.when_to_use}</p>
                )}
                <p className="mt-auto pt-3 text-[11.5px] text-muted-foreground">
                  {n === 0 ? 'Ningún agente la usa' : `La usa${n === 1 ? ' 1 agente' : `n ${n} agentes`}`}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
